// frontend/src/pages/ThreatRadarPage/ThreatRadarPage.jsx

import React, { useState, useContext, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { 
    Box, Typography, TextField, Autocomplete, Chip, 
    Button, FormControl, Select, MenuItem, Checkbox, 
    ListItemText, Tooltip, CircularProgress, Slider,
    IconButton, Drawer, InputLabel, OutlinedInput
} from '@mui/material';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';
import ReplayIcon from '@mui/icons-material/Replay';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

import { PLATFORM_ICONS, getPlatformDisplayName } from '../../components/PlatformUtils/PlatformUtils.jsx';
import { ALL_DASHBOARD_PLATFORMS } from '../AccountPage/AccountPage.jsx';
import { AuthContext } from '../../contexts/AuthContext';
import { UIContext } from '../../contexts/UIContext';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import HistoryListHeader from '../../components/HistoryListHeader/HistoryListHeader';
import ThreatItem from './ThreatItem';
import ExplanationModal from '../../components/ExplanationModal/ExplanationModal';
import HeatmapModal from '../../components/HeatmapModal/HeatmapModal';
import HistoryTextInspectionModal from '../../components/TextInspectionModal/HistoryTextInspectionModal';

import '../../pages/AnalysisHistoryPage/AnalysisHistoryPage.css';
import '../../components/HistoryFilters/HistoryFilters.css';
import '../../components/ContentInput/ContentInput.css';

// Helper function for order-agnostic array comparison
const areArraysEqual = (arr1, arr2) => {
    if (!arr1 || !arr2) return arr1 === arr2;
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
    if (arr1.length !== arr2.length) return false;
    const sortedArr1 = [...arr1].sort();
    const sortedArr2 = [...arr2].sort();
    return sortedArr1.every((value, index) => value === sortedArr2[index]);
};

const VALID_DATE_RANGES = ['all_time', 'last_30_days', 'last_7_days', 'last_24_hours'];

const ThreatRadarPage = () => {
    const { t, i18n } = useTranslation();
    const { fetchWithAuth, user } = useContext(AuthContext);
    const { setIsFaqModalOpen } = useContext(UIContext);
    const { data: tasksConfigData } = useQuery({
        queryKey: ['tasksConfig'],
        queryFn: async () => {
            const res = await fetch('/api/config/tasks');
            if (!res.ok) throw new Error('Failed to load tasks config');
            return res.json();
        },
        staleTime: Infinity, // Configuration never changes during session
    });
    const tasksConfig = useMemo(() => tasksConfigData?.tasks || {}, [tasksConfigData]);
    const masterTaskOrder = useMemo(() => tasksConfigData?.tasks ? Object.keys(tasksConfigData.tasks) : [], [tasksConfigData]);
    const threatRadarLimit = tasksConfigData?.threat_radar_limit || 100;

    const CONTENT_TYPE_OPTIONS = useMemo(() => ['image', 'video', 'audio', 'text'], []);
    const DEFAULT_TASKS = useMemo(() => ['fake_news_detection', 'scam_detection', 'brand_safety_analysis'], []);

    const sortedDashboardPlatforms = useMemo(() => {
        return [...ALL_DASHBOARD_PLATFORMS].sort((a, b) => 
            getPlatformDisplayName(a, t).localeCompare(getPlatformDisplayName(b, t))
        );
    }, [t, i18n.language]);

    const BASE_TASKS = useMemo(() => {
        if (!masterTaskOrder || !tasksConfig) return [];
        return masterTaskOrder.filter(taskKey => !tasksConfig[taskKey]?.is_meta_task);
    }, [masterTaskOrder, tasksConfig]);

    const defaultPrefs = useMemo(() => {
        const userPrefs = user?.threat_radar_preferences || {};
        
        // Check if the DB has an empty or legacy value (like 'previous_month')
        let safeDateRange = userPrefs.default_date_range;
        if (!safeDateRange || !VALID_DATE_RANGES.includes(safeDateRange)) {
            safeDateRange = 'last_7_days';
        }

        const displayedTasks = userPrefs.displayed_tasks || DEFAULT_TASKS;
        // Ensure default tasks only includes tasks that are currently visible/displayed
        const activeDefaultTasks = (userPrefs.default_tasks || DEFAULT_TASKS).filter(t => displayedTasks.includes(t));
        
        return {
            keywords: userPrefs.default_keywords || [],
            contentTypes: userPrefs.default_content_types || CONTENT_TYPE_OPTIONS,
            platforms: userPrefs.default_platforms || ALL_DASHBOARD_PLATFORMS,
            tasks: activeDefaultTasks.length > 0 ? activeDefaultTasks : displayedTasks,
            displayed_tasks: displayedTasks,
            scoreRange: userPrefs.default_score_range || [70, 100],
            sourceFilter: userPrefs.default_source_filter || ['scraped', 'public', 'local'],
            dateRange: safeDateRange,
            watermarked_only: userPrefs.watermarked_only || false
        };
    }, [user, CONTENT_TYPE_OPTIONS, DEFAULT_TASKS]);

    const [filters, setFilters] = useState(defaultPrefs);
    const [visibleCount, setVisibleCount] = useState(5);

    // State to trigger the search, and the TanStack Query
    const [submittedFilters, setSubmittedFilters] = useState(null);

    const { data: threatData = [], isFetching: isLoading } = useQuery({
        queryKey: ['threats', submittedFilters],
        queryFn: async () => {
            const response = await fetchWithAuth('/api/threat-radar/search', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(submittedFilters)
            });
            if (!response.ok) throw new Error('Search failed');
            return response.json();
        },
        enabled: !!submittedFilters, // ONLY runs when submittedFilters is set (i.e., button clicked)
        staleTime: 1000 * 60 * 5, // Cache the search results for 5 minutes
    });
    
    // UI states
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(true);
    const [isQueryDirty, setIsQueryDirty] = useState(true);

    // Modal States
    const [isExplanationModalOpen, setIsExplanationModalOpen] = useState(false);
    const [currentExplanation, setCurrentExplanation] = useState('');
    const [explanationTitle, setExplanationTitle] = useState('');
    const [isExplanationStructured, setIsExplanationStructured] = useState(false);
    const [explanationScore, setExplanationScore] = useState(null);
    const [explanationTaskKey, setExplanationTaskKey] = useState('digital_integrity_score');
    
    const [isHeatmapModalOpen, setIsHeatmapModalOpen] = useState(false);
    const [heatmapModalData, setHeatmapModalData] = useState({ imageSrc: '', regions: [], taskName: '', score: null, mode: 'full' });
    
    const [isTextInspectionModalOpen, setIsTextInspectionModalOpen] = useState(false);
    const [textInspectionModalData, setTextInspectionModalData] = useState({ regions: [], taskName: '', score: null });

    const [translatedModalExplanation, setTranslatedModalExplanation] = useState(null);
    const [isCheckingModalCache] = useState(false);
    const [isTranslatingModal, setIsTranslatingModal] = useState(false);
    const [modalTranslationError, setModalTranslationError] = useState(null);

    const [keywordInput, setKeywordInput] = useState('');
    // Format: { "Keyword1": "checking", "Keyword2": "en", "Keyword3": "non-en" }
    const [keywordLangStatus, setKeywordLangStatus] = useState({});
    const preventDrawerCloseRef = useRef(false);

    // Clear interval on unmount
    const pollIntervalRef = useRef(null);
    useEffect(() => {
        // Copy the ref to a local variable to satisfy the linter for the cleanup function
        const intervalId = pollIntervalRef.current;
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, []);

    // TanStack Mutation for Language Checking
    const checkLanguageMutation = useMutation({
        mutationFn: async (keywordsToCheck) => {
            const response = await fetchWithAuth('/api/threat-radar/check-language', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ keywords: keywordsToCheck })
            });
            if (!response.ok) throw new Error('Language check failed');
            return response.json();
        },
        onSuccess: (results) => {
            setKeywordLangStatus(prev => ({ ...prev, ...results }));
        },
        onError: (error, keywordsToCheck) => {
            setKeywordLangStatus(prev => {
                const newState = { ...prev };
                keywordsToCheck.forEach(kw => { newState[kw] = 'en'; });
                return newState;
            });
        }
    });

    const verifyKeywordLanguage = (keywordsToCheck) => {
        if (!keywordsToCheck || keywordsToCheck.length === 0) return;
        // Instantly set them to "checking" so the UI shows the spinner
        setKeywordLangStatus(prev => {
            const newState = { ...prev };
            keywordsToCheck.forEach(kw => { newState[kw] = 'checking'; });
            return newState;
        });
        checkLanguageMutation.mutate(keywordsToCheck);
    };

    // --- Filter Evaluation Logic ---
    const isKeywordsEdited = !areArraysEqual(filters.keywords, defaultPrefs.keywords);
    const isScoreRangeEdited = filters.scoreRange[0] !== defaultPrefs.scoreRange[0] || filters.scoreRange[1] !== defaultPrefs.scoreRange[1];
    const isSourceFilterEdited = !areArraysEqual(filters.sourceFilter, defaultPrefs.sourceFilter);
    const isContentTypesEdited = !areArraysEqual(filters.contentTypes, defaultPrefs.contentTypes);
    const isPlatformsEdited = !areArraysEqual(filters.platforms, defaultPrefs.platforms);
    const isTasksEdited = !areArraysEqual(filters.tasks, defaultPrefs.tasks);
    const isDateRangeEdited = filters.dateRange !== defaultPrefs.dateRange;
    const isWatermarkedOnlyEdited = filters.watermarked_only !== defaultPrefs.watermarked_only;

    const areFiltersApplied = isKeywordsEdited || isScoreRangeEdited || isSourceFilterEdited || isContentTypesEdited || isPlatformsEdited || isTasksEdited || isDateRangeEdited || isWatermarkedOnlyEdited;

    // --- HANDLERS ---
    const handleFilterChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
        setIsQueryDirty(true);
    };

    const handleSingleFilterReset = (name, defaultValue) => {
        setFilters(prev => ({ ...prev, [name]: defaultValue }));
        if (name === 'keywords') setKeywordInput('');
        setIsQueryDirty(true);
    };

    const handleResetFilters = () => {
        setFilters(defaultPrefs);
        setKeywordInput(''); // Clear input if user resets
        setIsQueryDirty(true);
    };

    // --- Commit pending keyword gracefully on Blur ---
    const handleCommitPendingKeyword = () => {
        if (!keywordInput) return;
        const val = keywordInput.trim();
        if (!val) return;

        // Flag that we are committing right now, so if a drawer close event fires instantly after, we ignore it
        preventDrawerCloseRef.current = true;
        setTimeout(() => {
            preventDrawerCloseRef.current = false;
        }, 150); // 150ms is plenty for the backdrop click event to finish bubbling

        // Prevent duplicates
        if (filters.keywords.some(k => k.toLowerCase() === val.toLowerCase())) {
            setKeywordInput('');
            return;
        }

        const newArr = [...filters.keywords, val];
        const lowercasedUnique = [...new Set(newArr.map(k => k.trim().toLowerCase()))].filter(k => k !== '');
        const limited = lowercasedUnique.slice(0, 10); // MAX_KEYWORDS
        
        // Only verify TRULY NEW keywords that aren't already in the list
        const addedKeywords = limited.filter(kw => !filters.keywords.includes(kw));
        
        handleFilterChange('keywords', limited);
        setKeywordInput(''); 
        
        if (addedKeywords.length > 0) {
            verifyKeywordLanguage(addedKeywords);
        }
    };

    // --- DRAWER HANDLER ---
    const handleDrawerClose = (event, reason) => {
        // If a keyword was literally just committed via the onBlur event, abort the close action
        if (preventDrawerCloseRef.current) {
            return;
        }
        
        // Fallback: If for some reason the state still has pending text
        if (keywordInput.trim() !== '') {
            handleCommitPendingKeyword();
            return;
        }
        
        setIsFilterDrawerOpen(false);
    };

    // --- SYNCHRONOUS QUERY ---
    const runQuery = () => {
        let currentKeywords = [...filters.keywords];
        
        // Grab any pending text that might be mid-blur
        if (keywordInput.trim() !== '') {
            const val = keywordInput.trim();
            if (!currentKeywords.some(k => k.toLowerCase() === val.toLowerCase())) {
                currentKeywords.push(val);
                const lowercasedUnique = [...new Set(currentKeywords.map(k => k.trim().toLowerCase()))].filter(k => k !== '');
                currentKeywords = lowercasedUnique.slice(0, 10);
                
                const addedKeywords = currentKeywords.filter(kw => !filters.keywords.includes(kw));
                handleFilterChange('keywords', currentKeywords);
                setKeywordInput('');
                
                if (addedKeywords.length > 0) {
                    verifyKeywordLanguage(addedKeywords);
                }
            } else {
                setKeywordInput('');
            }
        }

        if (currentKeywords.length === 0) return;
        
        // --- TRIGGER TANSTACK QUERY ---
        // By setting this state, the useQuery hook above automatically runs the fetch!
        setSubmittedFilters({ ...filters, keywords: currentKeywords });
        setIsQueryDirty(false);
        setVisibleCount(5); 

        if (window.innerWidth > 900) {
            setIsFilterDrawerOpen(false); // Close drawer to show wide content on complete
        }
    };

    const loadMore = () => setVisibleCount(prev => prev + 5);

    // --- MODAL HANDLERS ---
    const handleShowExplanation = useCallback((taskName, explanation, isStructured, score, rawTaskKey) => {
        setExplanationTitle(t('explanationModal.title', { taskName }));
        setCurrentExplanation(explanation);
        setIsExplanationStructured(isStructured);
        setExplanationScore(score);
        setExplanationTaskKey(rawTaskKey || 'digital_integrity_score');
        setIsExplanationModalOpen(true);
    }, [t]);

    const handleShowHeatmap = (imageSrc, regions, taskName, score, footerNote = null) => {
        setHeatmapModalData({ imageSrc, regions, taskName, score, footerNote, mode: 'history' });
        setIsHeatmapModalOpen(true);
    };

    const handleShowTextInspection = (regions, taskName, score) => {
        setTextInspectionModalData({ regions, taskName, score, mode: 'history' });
        setIsTextInspectionModalOpen(true);
    };

    const handleTranslateModalExplanation = async () => {
        if (!currentExplanation) return;
        setIsTranslatingModal(true);
        setModalTranslationError(null);
        try {
            const response = await fetchWithAuth('/api/translate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: currentExplanation, target_language: i18n.language })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Translation failed');
            setTranslatedModalExplanation(data.translated_text);
        } catch (err) {
            setModalTranslationError(err.message);
        } finally {
            setIsTranslatingModal(false);
        }
    };

    const currentDrawerWidth = areFiltersApplied ? 420 : 360;
    const MAX_KEYWORDS = 10;
    const isAtMaxKeywords = filters.keywords.length >= MAX_KEYWORDS;
    const isDuplicateKeyword = keywordInput.trim() !== '' && 
        filters.keywords.some(kw => kw.toLowerCase() === keywordInput.trim().toLowerCase());

    // --- Non-English Character Detection ---
    const wordCount = keywordInput.trim() ? keywordInput.trim().split(/\s+/).length : 0;
    const isTooManyWords = wordCount > 3;

    // Checks if the string contains any characters outside standard English letters, numbers, and basic punctuation
    const hasNonEnglishChars = keywordInput.trim() !== '' && !/^[\x20-\x7E]*$/.test(keywordInput);

    const showInputTooltip = (isAtMaxKeywords && keywordInput.length > 0) || isDuplicateKeyword || hasNonEnglishChars || isTooManyWords;
    let inputTooltipMessage = "";
    if (isAtMaxKeywords && keywordInput.length > 0) {
        inputTooltipMessage = t('maxKeywords', { max: MAX_KEYWORDS, defaultValue: `Maximum of ${MAX_KEYWORDS} keywords allowed` });
    } else if (isDuplicateKeyword) {
        inputTooltipMessage = t('ThreatRadarPage.filters.duplicateKeyword');
    } else if (isTooManyWords) {
        inputTooltipMessage = t('ThreatRadarPage.filters.maxWords', { defaultValue: 'Max 3 words allowed per keyword' });
    } else if (hasNonEnglishChars) {
        inputTooltipMessage = t('ThreatRadarPage.filters.englishWarning');
    }

    return (
        <div className={`history-page-wrapper ${isExplanationModalOpen ? 'app-content-blurred' : ''}`}>
            <Helmet><title>{t('ThreatRadarPage.helmet.title')}</title></Helmet>
            
            <main 
                className="history-main-content"
                style={{ 
                    marginLeft: (isFilterDrawerOpen && window.innerWidth > 900) ? `${currentDrawerWidth}px` : '0', 
                    width: (isFilterDrawerOpen && window.innerWidth > 900) ? `calc(100% - ${currentDrawerWidth}px)` : '100%',
                    transition: 'margin-left 0.3s ease, width 0.3s ease',
                    boxSizing: 'border-box'
                }}
            >
                <Header showDataFlowPreview={false} showBackButton={true} />
                <div className="history-content-inner">
                    <h1>{t('ThreatRadarPage.title')}</h1>
                    <p style={{ color: 'var(--secondary-color)' }}>
                        {t('ThreatRadarPage.description', { limit: threatRadarLimit })}
                    </p>
                    
                    {!user?.threat_radar_preferences?.active_monitoring && (
                        <Box sx={{ 
                            mt: 2, 
                            mb: 3, 
                            py: 1.25, 
                            px: 2, 
                            bgcolor: 'rgba(245, 158, 11, 0.06)', 
                            border: '1px solid rgba(245, 158, 11, 0.25)', 
                            borderRadius: '8px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between', 
                            gap: 2 
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <TravelExploreIcon sx={{ color: '#F59E0B', fontSize: '1.4rem', flexShrink: 0 }} />
                                <Typography
                                    component="div"
                                    variant="body2"
                                    sx={{
                                        color: 'rgba(255,255,255,0.9)',
                                        fontSize: '0.92rem',
                                        m: 0,
                                        p: 0,
                                        lineHeight: 1.4
                                    }}
                                >
                                    {t('ThreatRadarPage.promo.text', 'Want to hunt for these threats automatically? Enable Proactive Background Monitoring.')}
                                </Typography>
                            </Box>
                            <Button 
                                component={RouterLink} 
                                to="/app/account" 
                                state={{ scrollToThreatRadar: true, openProactiveMonitoring: true }} 
                                variant="contained" 
                                size="small" 
                                sx={{ 
                                    color: 'var(--primary-color)',
                                    bgcolor: '#F59E0B', 
                                    fontWeight: 600, 
                                    fontSize: '0.8rem',
                                    textTransform: 'none', 
                                    whiteSpace: 'nowrap',
                                    boxShadow: 'none',
                                    py: 0.6,
                                    px: 1.75,
                                    borderRadius: '6px',
                                    transition: 'all 0.2s ease',
                                    '&:hover': { 
                                        bgcolor: '#D97706',
                                        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)'
                                    } 
                                }}
                            >
                                {t('ThreatRadarPage.promo.button', 'Enable Automation')}
                            </Button>
                        </Box>
                    )}

                    <HistoryListHeader
                        onOpenFilters={() => setIsFilterDrawerOpen(true)}
                        onClearFilters={handleResetFilters}
                        areFiltersApplied={areFiltersApplied}
                    />

                    {/* RESULTS AREA */}
                    {isQueryDirty ? (
                        <div className="history-empty">
                            <SearchIcon sx={{ fontSize: 60, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
                            {filters.keywords.length === 0 ? (
                                <p>{t('ThreatRadarPage.empty.noKeywords')}</p>
                            ) : (
                                <p>{t('ThreatRadarPage.empty.readyToSearch')}</p>
                            )}
                        </div>
                        ) : isLoading ? (
                            <div className="history-status loading">
                                <CircularProgress 
                                    color="secondary" 
                                    aria-label={t('ThreatRadarPage.aria.loadingThreats')} 
                                />
                            </div>
                        ) : threatData.length > 0 ? (
                            <>
                                <div className="history-items-container">
                                    {threatData.slice(0, visibleCount).map((item, idx) => (
                                        <ThreatItem
                                            key={`${item.exact_hash}-${idx}`}
                                            item={item}
                                            onShowExplanation={handleShowExplanation} 
                                            onShowHeatmap={handleShowHeatmap}
                                            onShowTextInspection={handleShowTextInspection}
                                            activeScoreFilters={filters.tasks}
                                            forcedVerificationParams={item.forced_verification_params}
                                            trackedKeywords={filters.keywords}
                                        />
                                    ))}
                                </div>
                                {visibleCount < threatData.length && (
                                    <div className="load-more-container">
                                        <Button variant="outlined" onClick={loadMore} className="load-more-button">
                                            {t('ThreatRadarPage.buttons.loadMore')}
                                        </Button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="history-empty">
                                <SecurityIcon sx={{ fontSize: 60, color: '#4CAF50', mb: 2 }} />
                                <p>{t('ThreatRadarPage.empty.noThreats')}</p>
                            </div>
                        )}
                    </div>
                    <Footer onOpenFaqModal={() => setIsFaqModalOpen(true)} showFaqButton={true} />
                </main>

                {/* FULL HEIGHT OVERLAPPING FILTER DRAWER */}
                <Drawer 
                    anchor="left" 
                    open={isFilterDrawerOpen} 
                    onClose={handleDrawerClose}
                    PaperProps={{ sx: { backgroundColor: 'var(--tertiary-color)' } }} 
                >
                    <Box 
                        className="filter-drawer-container"
                        component="aside"
                        aria-label={t('ThreatRadarPage.filters.drawerTitle')}
                        sx={{ 
                            width: currentDrawerWidth, 
                            padding: 2.5, 
                            transition: 'width 0.3s ease' 
                        }}
                    >
                        <header className="filter-drawer-header">
                            <Typography variant="h6" component="h3">{t('ThreatRadarPage.filters.drawerTitle')}</Typography>
                            <IconButton onClick={handleDrawerClose} aria-label={t('ThreatRadarPage.filters.closeAria')}><CloseIcon /></IconButton>
                        </header>

                        <div className="filter-drawer-body">
                            {/* Keywords */}
                            <div className={`filter-item-wrapper ${isKeywordsEdited ? 'active-filter' : ''}`} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px', minHeight: 'auto' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Typography 
                                        className="filter-label" 
                                        sx={{ 
                                            fontSize: '0.85rem', 
                                            color: 'var(--primary-color)', 
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        {t('ThreatRadarPage.filters.trackedKeywords')}
                                        <span style={{ margin: '0 6px', fontWeight: 400, opacity: 0.8, color: 'var(--primary-color)' }}>
                                            ({filters.keywords.length}/{MAX_KEYWORDS})
                                        </span>
                                        <Tooltip title={t('ThreatRadarPage.filters.keywordsTooltip')} arrow>
                                            <InfoOutlinedIcon sx={{ fontSize: '14px', color: 'var(--primary-color)', mb: '1px' }} />
                                        </Tooltip>
                                    </Typography>
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Tooltip title={inputTooltipMessage} open={showInputTooltip} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
                                        <Box sx={{
                                            border: '1px solid rgba(150, 150, 150, 0.4)',
                                            borderRadius: '4px',
                                            maxHeight: '120px',
                                            overflowY: 'auto',
                                            overflowX: 'hidden',
                                            width: '320px',
                                            transition: 'border 0.2s ease',
                                            '&:hover': { borderColor: 'rgba(150, 150, 150, 0.87)' },
                                            '&:focus-within': { borderColor: 'var(--secondary-color)', borderWidth: '2px', m: '-1px' },
                                            '&::-webkit-scrollbar': { width: '6px' },
                                            '&::-webkit-scrollbar-track': { background: 'transparent' },
                                            '&::-webkit-scrollbar-thumb': { background: 'rgba(150, 150, 150, 0.4)', borderRadius: '10px' },
                                            '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(150, 150, 150, 0.7)' }
                                        }}>
                                            <Autocomplete
                                                multiple
                                                freeSolo
                                                disableClearable
                                                options={[]}
                                                value={filters.keywords}
                                                sx={{ width: '100%' }}
                                                onChange={(e, val) => {
                                                    const uniqueMap = new Map();
                                                    val.forEach(k => {
                                                        const trimmed = k.trim();
                                                        if (trimmed && !uniqueMap.has(trimmed.toLowerCase())) {
                                                            uniqueMap.set(trimmed.toLowerCase(), trimmed);
                                                        }
                                                    });
                                                    const limited = Array.from(uniqueMap.values()).slice(0, MAX_KEYWORDS);
                                                    
                                                    const addedKeywords = limited.filter(kw => !filters.keywords.includes(kw));
                                                    handleFilterChange('keywords', limited);
                                                    
                                                    if (addedKeywords.length > 0) {
                                                        verifyKeywordLanguage(addedKeywords);
                                                   }
                                                }}
                                                inputValue={keywordInput}
                                                onInputChange={(event, newInputValue, reason) => {
                                                    if (reason === 'input' || reason === 'clear') {
                                                        setKeywordInput(newInputValue);
                                                    } else if (reason === 'reset') {
                                                        setKeywordInput(''); 
                                                    }
                                                }}
                                                renderTags={(value, getTagProps) =>
                                                    value.map((option, index) => {
                                                        const failsFrontend = !/^[\x20-\x7E]*$/.test(option);
                                                        const status = keywordLangStatus[option];
                                                        const isChecking = status === 'checking';
                                                        const failsBackend = status && status !== 'en' && status !== 'checking';
                                                        const isNonEnglish = failsFrontend || failsBackend;

                                                        let detectedLangName = status;
                                                        if (failsBackend) {
                                                            try {
                                                                const displayNames = new Intl.DisplayNames([i18n.language || 'en'], { type: 'language' });
                                                                detectedLangName = displayNames.of(status);
                                                            } catch (e) {
                                                                detectedLangName = status.toUpperCase();
                                                            }
                                                        }

                                                        const chipTooltip = isNonEnglish ? (
                                                            <div style={{ padding: '2px' }}>
                                                                {t('ThreatRadarPage.filters.englishWarning')}
                                                                {failsBackend && (
                                                                    <span style={{ 
                                                                        display: 'block', 
                                                                        marginTop: '8px', 
                                                                        color: '#FFB142', 
                                                                        fontWeight: 'bold',
                                                                        textTransform: 'capitalize' 
                                                                    }}>
                                                                        {t('ThreatRadarPage.filters.detectedLanguage')}: {detectedLangName}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ) : option.length > 25 ? option : "";

                                                        return (
                                                            <Tooltip 
                                                                key={index}
                                                                title={chipTooltip} 
                                                                arrow 
                                                                disableHoverListener={!isNonEnglish && option.length <= 25}
                                                                PopperProps={{ className: 'global-dark-tooltip' }}
                                                            >
                                                                <Chip 
                                                                    variant="outlined" 
                                                                    {...getTagProps({ index })} 
                                                                    size="small" 
                                                                    label={
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                            <span style={{ 
                                                                                whiteSpace: 'nowrap', 
                                                                                overflow: 'hidden', 
                                                                                textOverflow: 'ellipsis',
                                                                                maxWidth: '220px',
                                                                                display: 'block' 
                                                                            }}>
                                                                                {option}
                                                                            </span>
                                                                            {isChecking && (
                                                                                <CircularProgress 
                                                                                    size={10} 
                                                                                    sx={{ color: 'inherit' }} 
                                                                                    aria-label={t('ThreatRadarPage.aria.checkingLanguage')}
                                                                                />
                                                                            )}
                                                                        </Box>
                                                                    }
                                                                    sx={{ 
                                                                        backgroundColor: isNonEnglish ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255,255,255,0.05)', 
                                                                        borderColor: isNonEnglish ? '#FF9800' : 'inherit',
                                                                        color: isNonEnglish ? '#FF9800' : 'inherit',
                                                                        fontWeight: 400,
                                                                        maxWidth: '100%',
                                                                        opacity: isChecking ? 0.6 : 1,
                                                                        transition: 'all 0.3s ease'
                                                                    }} 
                                                                />
                                                            </Tooltip>
                                                        );
                                                    })
                                                }
                                                renderInput={(params) => (
                                                    <TextField 
                                                        {...params} 
                                                        variant="outlined" 
                                                        size="small" 
                                                        placeholder={filters.keywords.length === 0 ? t('ThreatRadarPage.filters.typePlaceholder') : ""}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' && (isDuplicateKeyword || isTooManyWords)) {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                            }
                                                        }}
                                                        inputProps={{
                                                            ...params.inputProps,
                                                            'aria-label': t('ThreatRadarPage.filters.trackedKeywords'),
                                                            style: {
                                                                color: (isDuplicateKeyword || isTooManyWords) ? '#F44336' : (hasNonEnglishChars ? '#FF9800' : 'var(--primary-color)'),
                                                                fontWeight: 600
                                                            },
                                                            onBlur: (e) => {
                                                                if (params.inputProps.onBlur) {
                                                                    params.inputProps.onBlur(e);
                                                                }
                                                                handleCommitPendingKeyword();
                                                            }
                                                       }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                alignItems: 'flex-start',
                                                                padding: '6px',
                                                                '& fieldset': { display: 'none' }
                                                            },
                                                            '& .MuiAutocomplete-input': {
                                                                transition: 'color 0.2s ease',
                                                                padding: '2px 4px !important'
                                                            }
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Tooltip>
                                        
                                    {isKeywordsEdited && (
                                        <Tooltip title={t('ThreatRadarPage.filters.resetFilter')} arrow>
                                            <IconButton 
                                                className="single-reset-btn" 
                                                onClick={() => {
                                                    handleSingleFilterReset('keywords', defaultPrefs.keywords);
                                                    setKeywordInput('');
                                                }} 
                                                size="small" 
                                                sx={{ ml: 1 }}
                                            >
                                                <ReplayIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </div>

                            {/* Score Slider */}
                            <div className={`filter-item-wrapper ${isScoreRangeEdited ? 'active-filter' : ''}`} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0px', minHeight: 'auto' }}>
                                <Typography className="filter-label" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem', color: 'var(--primary-color)', fontWeight: 600, mb: '4px' }}>
                                    <span>{t('ThreatRadarPage.filters.scoreRange')} {filters.scoreRange[0]}% - {filters.scoreRange[1]}%</span>
                                    <Tooltip title={t('ThreatRadarPage.filters.tooltips.scoreRange')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                        <InfoOutlinedIcon sx={{ fontSize: '14px', color: 'var(--primary-color)', cursor: 'help', opacity: 0.7, '&:hover': { opacity: 1 } }} />
                                    </Tooltip>
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Box sx={{ width: '320px', px: 0.5, boxSizing: 'border-box', flexShrink: 0 }}>
                                        <Slider 
                                            value={filters.scoreRange} 
                                            onChange={(e, val) => handleFilterChange('scoreRange', val)} 
                                            valueLabelDisplay="auto" 
                                            min={0} 
                                            max={100}
                                            getAriaLabel={(index) => index === 0 ? t('ThreatRadarPage.filters.minScore') : t('ThreatRadarPage.filters.maxScore')}
                                            sx={{ 
                                                color: isScoreRangeEdited ? 'var(--primary-color)' : 'var(--secondary-color)',
                                                height: 4,
                                                mt: '-4px',
                                                transition: 'color 0.3s ease',
                                                '& .MuiSlider-thumb': {
                                                    width: 10,
                                                    height: 10,
                                                    transition: '0.3s cubic-bezier(.47,1.64,.41,.8)',
                                                    '&:before': { boxShadow: '0 2px 12px 0 rgba(0,0,0,0.4)' },
                                                },
                                                '& .MuiSlider-rail': { opacity: 0.3 },
                                            }} 
                                        />
                                    </Box>
                                    <Box sx={{ ml: '6px', display: 'flex', justifyContent: 'center', width: '36px' }}>
                                        {isScoreRangeEdited && (
                                            <Tooltip title={t('ThreatRadarPage.filters.resetFilter')} arrow>
                                                <IconButton 
                                                    className="single-reset-btn" 
                                                    onClick={() => handleSingleFilterReset('scoreRange', defaultPrefs.scoreRange)} 
                                                    size="small"
                                                    sx={{ p: 0 }}
                                                >
                                                    <ReplayIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            </div>

                            {/* Source Type(s) */}
                            <div className="filter-item-wrapper">
                                <FormControl className={`filter-form-control ${isSourceFilterEdited ? 'active-filter' : ''}`}>
                                    <InputLabel id="source-filter-label" sx={{ display: 'flex', alignItems: 'center', color: 'var(--primary-color)', '&.Mui-focused': { color: 'var(--primary-color)' } }}>
                                        {t('ThreatRadarPage.filters.sourceType')}
                                        <Tooltip title={t('ThreatRadarPage.filters.tooltips.sourceType')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                            <InfoOutlinedIcon 
                                                onMouseDown={(e) => e.stopPropagation()} 
                                                sx={{ fontSize: '15px', ml: 1, color: 'var(--primary-color)', opacity: 0.6, cursor: 'help', pointerEvents: 'auto', '&:hover': { opacity: 1 } }} 
                                            />
                                        </Tooltip>
                                    </InputLabel>
                                    <Select 
                                        labelId="source-filter-label"
                                        id="source-filter-select"
                                        multiple
                                        value={filters.sourceFilter} 
                                        onChange={(e) => {
                                            const selected = e.target.value;
                                            if (selected.length === 0) return;
                                            handleFilterChange('sourceFilter', selected);
                                        }}
                                        input={
                                            <OutlinedInput 
                                                label={
                                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                                        {t('ThreatRadarPage.filters.sourceType')}
                                                        <InfoOutlinedIcon sx={{ fontSize: '15px', ml: 1 }} />
                                                    </span>
                                                }
                                            />
                                        }
                                        renderValue={(selected) => {
                                            if (selected.length === 3) return t('ThreatRadarPage.filters.allSources', 'All Sources');
                                            if (selected.length === 2) return t('ThreatRadarPage.filters.selectedCount', { count: 2, defaultValue: '2 Selected' });
                                            if (selected[0] === 'scraped') return t('ThreatRadarPage.filters.sourceScraped', 'Automated Social Scrapes');
                                            if (selected[0] === 'public') return t('ThreatRadarPage.filters.publicUrls', 'Direct Public URLs');
                                            return t('ThreatRadarPage.filters.localUploads', 'My Local Uploads');
                                        }}
                                        MenuProps={{ className: 'history-multi-select-menu' }}
                                        sx={{
                                            '& .MuiSelect-select': {
                                                color: isSourceFilterEdited ? 'var(--primary-color) !important' : 'inherit',
                                            }
                                        }}
                                    >
                                        <MenuItem value="scraped" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Checkbox checked={filters.sourceFilter.includes('scraped')} size="small" />
                                                <ListItemText primary={t('ThreatRadarPage.filters.sourceScraped', 'Automated Social Scrapes (Global)')} />
                                            </Box>
                                            <Tooltip title={t('ThreatRadarPage.filters.tooltips.sourceScraped', 'Threats found autonomously by the background Radar.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                <InfoIcon sx={{ fontSize: '1.1rem', color: 'rgba(0,0,0,0.4)', ml: 1, '&:hover': { color: 'rgba(0,0,0,0.8)' } }} />
                                            </Tooltip>
                                        </MenuItem>
                                        <MenuItem value="public" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Checkbox checked={filters.sourceFilter.includes('public')} size="small" />
                                                <ListItemText primary={t('ThreatRadarPage.filters.publicUrls', 'Public URLs (Global)')} />
                                            </Box>
                                            <Tooltip title={t('ThreatRadarPage.filters.tooltips.sourcePublic', 'Threats analyzed manually by pasting a public link into the Analyzer.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                <InfoIcon sx={{ fontSize: '1.1rem', color: 'rgba(0,0,0,0.4)', ml: 1, '&:hover': { color: 'rgba(0,0,0,0.8)' } }} />
                                            </Tooltip>
                                        </MenuItem>
                                        <MenuItem value="local" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Checkbox checked={filters.sourceFilter.includes('local')} size="small" />
                                                <ListItemText primary={t('ThreatRadarPage.filters.localUploads', 'My Local Uploads (Private)')} />
                                            </Box>
                                            <Tooltip title={t('ThreatRadarPage.filters.tooltips.sourceLocal', 'Private files uploaded directly from your computer.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                <InfoIcon sx={{ fontSize: '1.1rem', color: 'rgba(0,0,0,0.4)', ml: 1, '&:hover': { color: 'rgba(0,0,0,0.8)' } }} />
                                            </Tooltip>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                {isSourceFilterEdited && (
                                    <Tooltip title={t('ThreatRadarPage.filters.resetFilter')} arrow>
                                        <IconButton className="single-reset-btn" onClick={() => handleSingleFilterReset('sourceFilter', defaultPrefs.sourceFilter)} size="small">
                                            <ReplayIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>

                            {/* Scan Area */}
                            <div className="filter-item-wrapper">
                                <FormControl className={`filter-form-control ${isWatermarkedOnlyEdited ? 'active-filter' : ''}`}>
                                    <InputLabel id="scan-area-label" sx={{ display: 'flex', alignItems: 'center', color: 'var(--primary-color)', '&.Mui-focused': { color: 'var(--primary-color)' } }}>
                                        {t('accountPage.threatRadarPrefs.scanArea', 'Scan Area')}
                                        <Tooltip title={t('ThreatRadarPage.filters.tooltips.scanArea')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                            <InfoOutlinedIcon 
                                                onMouseDown={(e) => e.stopPropagation()} 
                                                sx={{ fontSize: '15px', ml: 1, color: 'var(--primary-color)', opacity: 0.6, cursor: 'help', pointerEvents: 'auto', '&:hover': { opacity: 1 } }} 
                                            />
                                        </Tooltip>
                                    </InputLabel>
                                    <Select 
                                        labelId="scan-area-label"
                                        id="scan-area-select"
                                        value={filters.watermarked_only ? 'watermarked' : 'public'} 
                                        onChange={(e) => handleFilterChange('watermarked_only', e.target.value === 'watermarked')}
                                        input={
                                            <OutlinedInput 
                                                label={
                                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                                        {t('accountPage.threatRadarPrefs.scanArea')}
                                                        <InfoOutlinedIcon sx={{ fontSize: '15px', ml: 1 }} />
                                                    </span>
                                                } 
                                            />
                                        }
                                        MenuProps={{ className: 'history-multi-select-menu' }}
                                        sx={{
                                            '& .MuiSelect-select': {
                                                color: isWatermarkedOnlyEdited ? 'var(--primary-color) !important' : 'inherit',
                                            }
                                        }}
                                    >
                                        <MenuItem value="public">{t('accountPage.threatRadarPrefs.scanOptions.public')}</MenuItem>
                                        <MenuItem value="watermarked">{t('accountPage.threatRadarPrefs.scanOptions.watermarked')}</MenuItem>
                                    </Select>
                                </FormControl>
                                {isWatermarkedOnlyEdited && (
                                    <Tooltip title={t('ThreatRadarPage.filters.resetFilter')} arrow>
                                        <IconButton className="single-reset-btn" onClick={() => handleSingleFilterReset('watermarked_only', defaultPrefs.watermarked_only)} size="small">
                                            <ReplayIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>

                            {/* Platforms */}
                            <div className="filter-item-wrapper">
                                <FormControl className={`filter-form-control ${isPlatformsEdited ? 'active-filter' : ''}`}>
                                    <InputLabel id="platforms-label" sx={{ display: 'flex', alignItems: 'center', color: 'var(--primary-color)', '&.Mui-focused': { color: 'var(--primary-color)' } }}>
                                        {t('ThreatRadarPage.filters.platformLabel', 'Platform(s)')}
                                        <Tooltip title={t('ThreatRadarPage.filters.tooltips.platforms', 'Filter threats by the social network or search engine where they were discovered.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                            <InfoOutlinedIcon 
                                                onMouseDown={(e) => e.stopPropagation()} 
                                                sx={{ fontSize: '15px', ml: 1, color: 'var(--primary-color)', opacity: 0.6, cursor: 'help', pointerEvents: 'auto', '&:hover': { opacity: 1 } }} 
                                            />
                                        </Tooltip>
                                    </InputLabel>
                                    <Select 
                                        labelId="platforms-label"
                                        id="platforms-select"
                                        multiple 
                                        value={filters.platforms || ALL_DASHBOARD_PLATFORMS} 
                                        onChange={(e) => {
                                            const selected = e.target.value;
                                            if (selected.length === 0) return;
                                            handleFilterChange('platforms', selected);
                                        }}
                                        input={
                                            <OutlinedInput 
                                                label={
                                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                                        {t('ThreatRadarPage.filters.platformLabel', 'Platform(s)')}
                                                        <InfoOutlinedIcon sx={{ fontSize: '15px', ml: 1 }} />
                                                    </span>
                                                }
                                            />
                                        }
                                        renderValue={(selected) => {
                                            if (selected.length === ALL_DASHBOARD_PLATFORMS.length) return t('ThreatRadarPage.filters.allPlatforms', 'All Platforms');
                                            return t('ThreatRadarPage.filters.selectedCount', { count: selected.length, defaultValue: `${selected.length} Selected` });
                                        }}
                                        MenuProps={{ 
                                            className: 'history-multi-select-menu',
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 350
                                                }
                                            }
                                        }}
                                    >
                                        {sortedDashboardPlatforms.map((plat) => {
                                            const isSelected = (filters.platforms || ALL_DASHBOARD_PLATFORMS).includes(plat);
                                            const isLastRemaining = isSelected && (filters.platforms || ALL_DASHBOARD_PLATFORMS).length === 1;

                                            return (
                                                <MenuItem 
                                                    key={plat} 
                                                    value={plat}
                                                    sx={{ 
                                                        cursor: isLastRemaining ? 'not-allowed' : 'pointer',
                                                        '& *': { cursor: isLastRemaining ? 'not-allowed !important' : 'inherit' }
                                                    }}
                                                >
                                                    <Tooltip 
                                                        title={isLastRemaining ? t('accountPage.threatRadarPrefs.minOnePlatform', 'At least 1 platform must be selected') : ""} 
                                                        placement="left" 
                                                        arrow 
                                                        disableHoverListener={!isLastRemaining}
                                                        PopperProps={{ className: 'global-dark-tooltip' }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                            <Checkbox checked={isSelected} size="small" />
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {PLATFORM_ICONS[plat.toLowerCase()]}
                                                                <ListItemText primary={getPlatformDisplayName(plat, t)} />
                                                            </Box>
                                                        </Box>
                                                    </Tooltip>
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                                {isPlatformsEdited && (
                                    <Tooltip title={t('ThreatRadarPage.filters.resetFilter')} arrow>
                                        <IconButton className="single-reset-btn" onClick={() => handleSingleFilterReset('platforms', defaultPrefs.platforms)} size="small">
                                            <ReplayIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>

                            {/* Content Types */}
                            <div className="filter-item-wrapper">
                                <FormControl className={`filter-form-control ${isContentTypesEdited ? 'active-filter' : ''}`}>
                                    <InputLabel id="content-types-label" sx={{ display: 'flex', alignItems: 'center', color: 'var(--primary-color)', '&.Mui-focused': { color: 'var(--primary-color)' } }}>
                                        {t('historyFilters.contentType.label')}
                                        <Tooltip title={t('ThreatRadarPage.filters.tooltips.contentTypes')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                            <InfoOutlinedIcon 
                                                onMouseDown={(e) => e.stopPropagation()} 
                                                sx={{ fontSize: '15px', ml: 1, color: 'var(--primary-color)', opacity: 0.6, cursor: 'help', pointerEvents: 'auto', '&:hover': { opacity: 1 } }} 
                                            />
                                        </Tooltip>
                                    </InputLabel>
                                    <Select 
                                        labelId="content-types-label"
                                        id="content-types-select"
                                        multiple 
                                        value={filters.contentTypes} 
                                        onChange={(e) => handleFilterChange('contentTypes', e.target.value)}
                                        input={
                                            <OutlinedInput 
                                                label={
                                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                                        {t('historyFilters.contentType.label')}
                                                        <InfoOutlinedIcon sx={{ fontSize: '15px', ml: 1 }} />
                                                    </span>
                                                }
                                            />
                                        }
                                        renderValue={(selected) => {
                                            if (selected.length === CONTENT_TYPE_OPTIONS.length) {
                                                return t('historyFilters.contentType.options.allContentTypes'); 
                                            }
                                            return selected.map(s => t(`historyFilters.contentType.options.${s}`)).join(', ');
                                        }}
                                        MenuProps={{ className: 'history-multi-select-menu' }}
                                    >
                                        {CONTENT_TYPE_OPTIONS.map((opt) => (
                                            <MenuItem key={opt} value={opt}>
                                                <Checkbox checked={filters.contentTypes.indexOf(opt) > -1} size="small" />
                                                <ListItemText primary={t(`historyFilters.contentType.options.${opt}`)} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {isContentTypesEdited && (
                                    <Tooltip title={t('ThreatRadarPage.filters.resetFilter')} arrow>
                                        <IconButton className="single-reset-btn" onClick={() => handleSingleFilterReset('contentTypes', defaultPrefs.contentTypes)} size="small">
                                            <ReplayIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>

                            {/* Tasks */}
                            <div className="filter-item-wrapper">
                                <FormControl className={`filter-form-control ${isTasksEdited ? 'active-filter' : ''}`}>
                                    <InputLabel id="tasks-label" sx={{ display: 'flex', alignItems: 'center', color: 'var(--primary-color)', '&.Mui-focused': { color: 'var(--primary-color)' } }}>
                                        {t('historyFilters.analysisScore.label')}
                                        <Tooltip title={t('ThreatRadarPage.filters.tooltips.analysisScores')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                            <InfoOutlinedIcon 
                                                onMouseDown={(e) => e.stopPropagation()} 
                                                sx={{ fontSize: '15px', ml: 1, color: 'var(--primary-color)', opacity: 0.6, cursor: 'help', pointerEvents: 'auto', '&:hover': { opacity: 1 } }} 
                                            />
                                        </Tooltip>
                                    </InputLabel>
                                    <Select 
                                        labelId="tasks-label"
                                        id="tasks-select"
                                        multiple 
                                        value={filters.tasks} 
                                        onChange={(e) => handleFilterChange('tasks', e.target.value)} 
                                        input={
                                            <OutlinedInput 
                                                label={
                                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                                        {t('historyFilters.analysisScore.label')}
                                                        <InfoOutlinedIcon sx={{ fontSize: '15px', ml: 1 }} />
                                                    </span>
                                                }
                                            />
                                        }
                                        renderValue={(selected) => {
                                            // Compare against BASE_TASKS.length (Total tasks in app), 
                                            // not displayed_tasks.length, so it correctly says "3 Selected"
                                            if (selected.length === BASE_TASKS.length) {
                                                return t('historyFilters.analysisScore.options.allAnalysisScores');
                                            }
                                            return t('ThreatRadarPage.filters.selectedCount', { count: selected.length });
                                        }}
                                        MenuProps={{ className: 'history-multi-select-menu' }}
                                    >
                                        {(filters.displayed_tasks || []).map((taskKey) => (
                                            <MenuItem key={taskKey} value={taskKey} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Checkbox checked={filters.tasks.indexOf(taskKey) > -1} size="small" />
                                                    <ListItemText primary={t(`resultDisplay.analysisTasks.${taskKey}`, { defaultValue: taskKey })} />
                                                </Box>
                                                <Tooltip title={t(`verificationPage.scoreDefinitions.${taskKey}`, { defaultValue: tasksConfig?.[taskKey]?.description || '' })} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                    <InfoIcon sx={{ fontSize: '1.1rem', color: 'rgba(0,0,0,0.4)', ml: 1, '&:hover': { color: 'rgba(0,0,0,0.8)' } }} />
                                                </Tooltip>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {isTasksEdited && (
                                    <Tooltip title={t('ThreatRadarPage.filters.resetFilter')} arrow>
                                        <IconButton className="single-reset-btn" onClick={() => handleSingleFilterReset('tasks', defaultPrefs.tasks)} size="small">
                                            <ReplayIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>

                            {/* Date Range (Replaces Sort By) */}
                            <div className="filter-item-wrapper">
                                <FormControl className={`filter-form-control ${isDateRangeEdited ? 'active-filter' : ''}`}>
                                    <InputLabel id="date-range-label" sx={{ display: 'flex', alignItems: 'center', color: 'var(--primary-color)', '&.Mui-focused': { color: 'var(--primary-color)' } }}>
                                        {t('ThreatRadarPage.filters.dateRangeLabel')}
                                        <Tooltip title={t('ThreatRadarPage.filters.tooltips.dateRange')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                            <InfoOutlinedIcon 
                                                onMouseDown={(e) => e.stopPropagation()} 
                                                sx={{ fontSize: '15px', ml: 1, color: 'var(--primary-color)', opacity: 0.6, cursor: 'help', pointerEvents: 'auto', '&:hover': { opacity: 1 } }} 
                                            />
                                        </Tooltip>
                                    </InputLabel>
                                    <Select 
                                        labelId="date-range-label"
                                        id="date-range-select"
                                        value={filters.dateRange} 
                                        onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                                        input={
                                            <OutlinedInput 
                                                label={
                                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                                        {t('ThreatRadarPage.filters.dateRangeLabel')}
                                                        <InfoOutlinedIcon sx={{ fontSize: '15px', ml: 1 }} />
                                                    </span>
                                                }
                                            />
                                        }
                                        MenuProps={{ className: 'history-multi-select-menu' }}
                                    >
                                        <MenuItem value="all_time">{t('ThreatRadarPage.filters.dateRangeOptions.all_time')}</MenuItem>
                                        <MenuItem value="last_30_days">{t('ThreatRadarPage.filters.dateRangeOptions.last_30_days')}</MenuItem>
                                        <MenuItem value="last_7_days">{t('ThreatRadarPage.filters.dateRangeOptions.last_7_days')}</MenuItem>
                                        <MenuItem value="last_24_hours">{t('ThreatRadarPage.filters.dateRangeOptions.last_24_hours')}</MenuItem>
                                    </Select>
                                </FormControl>
                                {isDateRangeEdited && (
                                    <Tooltip title={t('ThreatRadarPage.filters.resetFilter')} arrow>
                                        <IconButton className="single-reset-btn" onClick={() => handleSingleFilterReset('dateRange', defaultPrefs.dateRange)} size="small">
                                            <ReplayIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="reset-button-container" style={{ flexDirection: 'column', gap: '16px' }}>
                            {(() => {
                                const hasValidKeywords = filters.keywords.length > 0 || keywordInput.trim() !== '';
                                const isRunQueryDisabled = isLoading || !hasValidKeywords || (!isQueryDirty && keywordInput.trim() === '');
                               
                                const getRunQueryTooltip = () => {
                                    if (isLoading) return "";
                                    if (!hasValidKeywords) {
                                        return t('ThreatRadarPage.filters.tooltips.noKeywords');
                                    }
                                    if (!isQueryDirty && keywordInput.trim() === '') {
                                        return t('ThreatRadarPage.filters.tooltips.noChanges');
                                    }
                                    return "";
                                };

                                return (
                                    <Tooltip 
                                        title={getRunQueryTooltip()} 
                                        arrow 
                                        placement="top" 
                                        disableHoverListener={!isRunQueryDisabled}
                                    >
                                        <span style={{ display: "block", width: "100%" }}>
                                            <button 
                                                className="analyze-button"
                                                onClick={runQuery}
                                                disabled={isRunQueryDisabled}
                                                style={{ 
                                                    width: '100%', 
                                                    margin: 0,
                                                    backgroundColor: isRunQueryDisabled ? 'rgba(0, 0, 0, 0.08)' : 'var(--secondary-color)',
                                                    color: isRunQueryDisabled ? 'rgba(0, 0, 0, 0.35)' : 'white',
                                                    cursor: isRunQueryDisabled ? 'not-allowed' : 'pointer',
                                                    pointerEvents: isRunQueryDisabled ? 'none' : 'auto'
                                                }}
                                            >
                                                {isLoading ? t('ThreatRadarPage.buttons.scanning') : t('ThreatRadarPage.buttons.scanDatabase')}
                                            </button>
                                        </span>
                                    </Tooltip>
                                );
                            })()}

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Button variant="text" size="small" onClick={handleResetFilters} startIcon={<ReplayIcon fontSize="small" />} sx={{ color: 'var(--secondary-color)', textTransform: 'none' }}>
                                    {t('ThreatRadarPage.buttons.resetAll')}
                                </Button>
                                <Tooltip title={t('ThreatRadarPage.buttons.editDefaultsTooltip')} arrow>
                                    <Button component={RouterLink} to="/app/account" state={{ scrollToThreatRadar: true }} variant="text" size="small" startIcon={<SettingsIcon />} sx={{ color: 'var(--secondary-color)', textTransform: 'none' }}>
                                        {t('ThreatRadarPage.buttons.editDefaults')}
                                    </Button>
                                </Tooltip>
                            </Box>
                        </div>
                    </Box>
                </Drawer>
                
                {/* MODALS */}
                <ExplanationModal
                    isOpen={isExplanationModalOpen}
                    onClose={() => setIsExplanationModalOpen(false)}
                    title={explanationTitle}
                    explanation={currentExplanation}
                    isStructured={isExplanationStructured}
                    score={explanationScore}
                    taskKey={explanationTaskKey}
                    translatedExplanation={translatedModalExplanation}
                    isCheckingCache={isCheckingModalCache}
                    isTranslating={isTranslatingModal}
                    translationError={modalTranslationError}
                    onTranslate={handleTranslateModalExplanation}
                />
                <HeatmapModal
                    isOpen={isHeatmapModalOpen}
                    onClose={() => setIsHeatmapModalOpen(false)}
                    imageSrc={heatmapModalData.imageSrc}
                    regions={heatmapModalData.regions}
                    taskName={heatmapModalData.taskName}
                    score={heatmapModalData.score}
                    mode={heatmapModalData.mode}
                    footerNote={heatmapModalData.footerNote}
                />
                <HistoryTextInspectionModal
                    isOpen={isTextInspectionModalOpen}
                    onClose={() => setIsTextInspectionModalOpen(false)}
                    regions={textInspectionModalData.regions}
                    taskName={textInspectionModalData.taskName}
                    score={textInspectionModalData.score}
                />
            </div>
        );
};

export default ThreatRadarPage;